import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useSecteur } from '../../contexts/SecteurContext';
import { FULL_DAY_HOURS } from '../../config/timeConstants';
import { differenceInHours, differenceInMinutes, format } from 'date-fns';
import HoursInput from '../HoursInput'; // Ajout de l'import

export default function PointageSelector({ eventId, date, endDate, allDay, onPointageChange, newPointage, canEdit }) {
  const { selectedSecteur } = useSecteur();
  const [users, setUsers] = useState([]);
  const [releveCodes, setReleveCodes] = useState([]);
  const [eventPointages, setEventPointages] = useState([]);
  const [activities, setActivities] = useState([]); // Ajout du state pour les activités
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Charger les utilisateurs du secteur
  useEffect(() => {
    const fetchUsers = async () => {
      const { data, error } = await supabase
        .from('user_mandates')
        .select(`
          id,
          users!inner (
            id,
            full_name
          )
        `)
        .eq('sector_id', selectedSecteur)
        .eq('is_active', true);

      if (!error && data) {
        const uniqueUsers = [...new Map(
          data.map(mandate => [mandate.users.id, mandate.users])
        ).values()];
        setUsers(uniqueUsers);
      }
    };

    if (selectedSecteur) {
      fetchUsers();
    }
  }, [selectedSecteur]);

  // Charger les codes relève pour l'utilisateur sélectionné
  useEffect(() => {
    const fetchReleveCodes = async () => {
      if (!newPointage.userId) return;

      const { data, error } = await supabase
        .from('user_mandates')
        .select(`
          releve_codes (
            id,
            code,
            role,
            color
          )
        `)
        .eq('user_id', newPointage.userId)
        .eq('sector_id', selectedSecteur)
        .eq('is_active', true);

      if (!error && data) {
        const codes = data
          .map(m => m.releve_codes)
          .filter(Boolean);
        setReleveCodes(codes);
      }
    };

    fetchReleveCodes();
  }, [newPointage.userId, selectedSecteur]);

  // Charger les pointages existants de l'événement
  useEffect(() => {
    const fetchEventPointages = async () => {
      if (!eventId) return;

      try {
        const { data: eventPointagesData, error: eventError } = await supabase
          .from('event_pointages')
          .select('pointage_id')
          .eq('event_id', eventId);

        if (eventError) throw eventError;

        if (!eventPointagesData?.length) {
          setEventPointages([]);
          return;
        }

        const pointageIds = eventPointagesData.map(ep => ep.pointage_id);
        const { data: pointagesData, error: pointagesError } = await supabase
          .from('pointage')
          .select(`
            id,
            hours,
            activity,
            on_report,
            user_mandate:user_mandate_id (
              id,
              user:user_id (
                id,
                full_name
              )
            ),
            releve_code:releve_code_id (
              id,
              code,
              role
            ),
            activities:activity (
              id,
              name
            )
          `)
          .in('id', pointageIds);

        if (pointagesError) throw pointagesError;

        const formattedPointages = pointagesData.map(p => ({
          id: p.id,
          hours: p.hours,
          on_report: p.on_report,
          user_mandate_id: p.user_mandate_id,
          releve_code_id: p.releve_code_id,
          activity_id: p.activity,
          users: {
            id: p.user_mandate?.user?.id,
            full_name: p.user_mandate?.user?.full_name || 'Utilisateur inconnu'
          },
          releve_codes: {
            id: p.releve_code?.id,
            code: p.releve_code?.code || 'Code inconnu',
            role: p.releve_code?.role || ''
          },
          activity: {
            id: p.activities?.id,
            name: p.activities?.name || 'Activité inconnue'
          }
        }));

        setEventPointages(formattedPointages);
      } catch (error) {
        console.error('Erreur lors de la récupération des pointages:', error);
        setEventPointages([]);
      }
    };

    if (eventId) {
      fetchEventPointages();
    }
  }, [eventId]);

  // Ajouter un useEffect pour charger les activités
  useEffect(() => {
    const fetchActivities = async () => {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .order('name');

      if (!error && data) {
        setActivities(data);
      }
    };

    fetchActivities();
  }, []);

  // Calculer les heures en fonction de la durée de l'événement
  const calculateEventDuration = useCallback(() => {
    if (!date || !endDate) return FULL_DAY_HOURS;
    
    if (allDay) return FULL_DAY_HOURS;

    const startDate = new Date(date);
    const endDateTime = new Date(endDate);
    
    // Calculer la différence en heures et minutes
    const hours = differenceInHours(endDateTime, startDate);
    const remainingMinutes = differenceInMinutes(endDateTime, startDate) % 60;
    
    // Convertir en format décimal (ex: 1h30 = 1.5)
    return hours + (remainingMinutes / 60);
  }, [date, endDate, allDay]);

  useEffect(() => {
    // Mettre à jour les heures du pointage quand la durée change
    const duration = calculateEventDuration();
    onPointageChange(prev => ({
      ...prev,
      hours: duration
    }));
  }, [calculateEventDuration, onPointageChange]);

  if (!eventId) return null;

  const handleAddPointage = async () => {
    try {
      if (!newPointage.userId || !newPointage.releveCodeId || !newPointage.activityId) {
        throw new Error('Veuillez sélectionner un utilisateur, un code et une activité');
      }

      // Récupérer le mandat de l'utilisateur
      const { data: mandateData, error: mandateError } = await supabase
        .from('user_mandates')
        .select(`id, releve_codes!inner (*)`)
        .eq('user_id', newPointage.userId)
        .eq('sector_id', selectedSecteur)
        .eq('releve_code_id', newPointage.releveCodeId)
        .single();

      if (mandateError) throw mandateError;

      const duration = calculateEventDuration();

      // Utiliser la date de début de l'événement
      const pointageToCreate = {
        user_mandate_id: mandateData.id,
        releve_code_id: newPointage.releveCodeId,
        activity: newPointage.activityId,
        hours: parseFloat(newPointage.hours || duration),
        date: format(date, 'yyyy-MM-dd') // Toujours utiliser la date de début de l'événement
      };

      // Insérer le pointage avec toutes les relations nécessaires
      const { data: pointageData, error: pointageError } = await supabase
        .from('pointage')
        .insert(pointageToCreate)
        .select(`
          id,
          hours,
          date,
          description,
          activity,
          user_mandate:user_mandate_id (
            id,
            user:user_id (
              id,
              full_name
            )
          ),
          releve_codes:releve_code_id (
            id,
            code,
            color,
            role,
            monthly_limit
          ),
          activities:activity (
            id,
            name
          )
        `)
        .single();

      if (pointageError) throw pointageError;

      // Lier le pointage à l'événement
      if (eventId) {
        const { error: linkError } = await supabase
          .from('event_pointages')
          .insert({
            event_id: eventId,
            pointage_id: pointageData.id
          });

        if (linkError) throw linkError;
      }

      // Formater le pointage avec toutes les données nécessaires
      const formattedPointage = {
        id: pointageData.id,
        hours: pointageData.hours,
        date: pointageData.date,
        description: pointageData.description,
        activity: pointageData.activities,  // S'assurer que l'activité est incluse
        releve_codes: pointageData.releve_codes,
        users: pointageData.user_mandate.user,
        backgroundColor: pointageData.releve_codes.color
      };

      // Mettre à jour la liste des pointages
      setEventPointages(prev => [...prev, formattedPointage]);

      // Réinitialiser le formulaire
      onPointageChange({
        userId: '',
        releveCodeId: '',
        activityId: '',
        hours: calculateEventDuration()
      });

    } catch (error) {
      console.error('Erreur lors de l\'ajout du pointage:', error);
    }
  };

  const handleDeletePointage = async (pointageId) => {
    try {
      // Supprimer d'abord la liaison
      if (eventId) {
        const { error: unlinkError } = await supabase
          .from('event_pointages')
          .delete()
          .match({ event_id: eventId, pointage_id: pointageId });

        if (unlinkError) throw unlinkError;
      }

      // Supprimer le pointage
      const { error: deleteError } = await supabase
        .from('pointage')
        .delete()
        .eq('id', pointageId);

      if (deleteError) throw deleteError;

      // Mettre à jour la liste des pointages
      setEventPointages(eventPointages.filter(p => p.id !== pointageId));

    } catch (error) {
      console.error('Erreur lors de la suppression du pointage:', error);
    }
  };

  const handleToggleOnReport = async (pointageId, currentValue) => {
    try {
      const { error } = await supabase
        .from('pointage')
        .update({ on_report: !currentValue })
        .eq('id', pointageId);

      if (error) throw error;

      // Mettre à jour l'état local
      setEventPointages(eventPointages.map(p => 
        p.id === pointageId ? { ...p, on_report: !currentValue } : p
      ));
    } catch (error) {
      console.error('Erreur lors de la modification du statut de rapport:', error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 900,
        mx: 'auto'
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: isMobile ? 1 : 2,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Typography variant="h6" gutterBottom>
          Ajouter un participant
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: isMobile ? 'stretch' : 'flex-start',
            width: '100%',
            flexWrap: isMobile ? 'nowrap' : 'wrap'
          }}
        >
          <FormControl fullWidth sx={isMobile ? { minWidth: 0 } : {}}>
            <InputLabel id="participant-label">Participant</InputLabel>
            <Select
              labelId="participant-label"
              id="participant-select"
              name="participant"
              value={newPointage.userId || ''}
              onChange={(e) => onPointageChange({ ...newPointage, userId: e.target.value })}
              label="Participant"
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={isMobile ? { minWidth: 0 } : {}}>
            <InputLabel id="releve-code-label">Code relève</InputLabel>
            <Select
              labelId="releve-code-label"
              id="releve-code-select"
              name="releveCode"
              value={newPointage.releveCodeId || ''}
              onChange={(e) => onPointageChange({ ...newPointage, releveCodeId: e.target.value })}
              label="Code relève"
            >
              {releveCodes.map((code) => (
                <MenuItem key={code.id} value={code.id}>
                  {code.code} ({code.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={isMobile ? { minWidth: 0 } : {}}>
            <InputLabel id="activity-label">Activité</InputLabel>
            <Select
              labelId="activity-label"
              id="activity-select"
              name="activity"
              value={newPointage.activityId || ''}
              onChange={(e) => onPointageChange({ ...newPointage, activityId: e.target.value })}
              label="Activité"
            >
              {activities.map((activity) => (
                <MenuItem key={activity.id} value={activity.id}>
                  {activity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={
              isMobile
                ? { width: '100%' }
                : { minWidth: '250px', flex: '0 0 250px' }
            }
          >
            <HoursInput
              value={newPointage.hours || calculateEventDuration()}
              onChange={(value) => onPointageChange({ ...newPointage, hours: value })}
              fullWidth
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPointage}
            sx={
              isMobile
                ? { width: '100%', height: '40px', minWidth: 0 }
                : { minWidth: '150px', height: '56px' }
            }
            disabled={!canEdit}
          >
            Ajouter
          </Button>
        </Box>
      </Paper>

      <List
        sx={{
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          p: 0,
        }}
      >
        {eventPointages.map((pointage) => (
          <ListItem
            key={pointage.id}
            sx={{
              px: 0,
              width: '100%',
              display: 'block',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                alignItems: { xs: 'flex-start', sm: 'center' },
                width: '100%',
                py: 1,
                position: 'relative'
              }}
            >
              <Typography sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word' }}>
                {pointage.users?.full_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word' }}>
                {pointage.releve_codes?.code}
                {pointage.releve_codes?.role ? ` (${pointage.releve_codes.role})` : ''}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word' }}>
                {pointage.activity?.name}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  minWidth: 0,
                  flex: 'none',
                  ml: { xs: 0, sm: 2 },
                  mt: { xs: 1, sm: 0 },
                  gap: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={pointage.on_report || false}
                      onChange={() => handleToggleOnReport(pointage.id, pointage.on_report)}
                      disabled={!canEdit}
                    />
                  }
                  label="Fait Marquant"
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" sx={{ minWidth: 0, textAlign: { xs: 'left', sm: 'right' } }}>
                  {pointage.hours}h
                </Typography>
                {canEdit && (
                  <IconButton
                    edge="end"
                    onClick={() => handleDeletePointage(pointage.id)}
                    size="small"
                    sx={{
                      ml: 1,
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
